
import React, { useState, useEffect } from "react";
import { Card, Button, Table, Form, Row, Col, Container, ListGroup } from "react-bootstrap";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import axios from 'axios'; // For API calls // Use a card validation library
import { toast } from "react-toastify";
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useSelector } from "react-redux";
import 'react-credit-cards/es/styles-compiled.css';
import PaymentMethod from "./paymentMethod";
import BillingAlerts from "./billingAlert";
import { Country, State, City } from "country-state-city";
import { BillingAPI } from "../../helpers/firebase_helper";

const BillingDetails = ({ billing }) => {
  const profileData = useSelector((state) => state.Profile.profileData);
  const [isEditing, setIsEditing] = useState(false);
  const [savedBillingDetails, setSavedBillingDetails] = useState(null);
  const [newBillingDetails, setNewBillingDetails] = useState({
    organization_type: '',
    firstname: '',
    lastname: '',
    phone_number: '',
    email: '',
    country: '',
    state: "",
    city: "",
    address: "",
    zipcode: "",
    gst_number: "",
  });

  const countries = Country.getAllCountries();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [cognito_id, setCognitoId] = useState(null);
  useEffect(() => {
    const fetchUser = async () => {
      const result = profileData?.result?.cognito_id; 
      
      setCognitoId(result); 
      if (cognito_id) {
        fetchBillingDetails();
      }

    };

    const fetchBillingDetails = async () => {
      try {
        const response = await BillingAPI.fetchBillingInfo(cognito_id);
        // console.log("billing fetch", response);

        if (response) {
          setSavedBillingDetails(response.result);
          setNewBillingDetails(response.result); 
        }
      } catch (error) {
        console.error("Error fetching billing details:", error);
        toast.error("Failed to load billing details.");
      }
    };
    fetchUser();

  }, [profileData, cognito_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBillingDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setNewBillingDetails((prev) => ({
      ...prev,
      country: selectedCountry,
      state: statesList.length ? "" : selectedCountry,
      city: "",
    }));
    const statesList = State.getStatesOfCountry(selectedCountry);
    setStates(statesList);
    setCities([]);
  };

  useEffect(()=> {
      if(savedBillingDetails?.country){
        const statesList = State.getStatesOfCountry(savedBillingDetails?.country);
      setStates(statesList);
      }
    },[savedBillingDetails])
  
  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setNewBillingDetails((prev) => ({
      ...prev,
      state: selectedState,
      city: "",
    }));
    setCities(City.getCitiesOfState(newBillingDetails.country, selectedState));
  };

  const handleSaveBillingDetails = async () => {
    const requiredFields = [
      "organization_type",
      "firstname",
      "lastname",
      "phone_number",
      "email",
      "country",
      "state",
      "city",
      "address",
      "zipcode",
    ];

    for (let field of requiredFields) {
      if (!newBillingDetails[field]) {
        toast.error(`Please fill in the ${field} field.`);
        return;
      }
    }

    try {
      const response = await BillingAPI.updateBillingInfo(cognito_id, newBillingDetails);

      // console.log(response);

      setSavedBillingDetails(newBillingDetails);
      toast.success(response?.message || "Billing details updated successfully.");
      setIsEditing(false);

    } catch (error) {
      console.error("Error saving billing details:", error);
      toast.error("Failed to save billing details.");
    }
  };

  const fieldLabels = {
    address: "Address",
    city: "City",
    country: "Country",
    email: "Email",
    firstname: "First Name",
    gst_number: "GST/VAT Number",
    lastname: "Last Name",
    organization_type: "Organization Type",
    phone_number: "Phone Number",
    state: "State",
    zipcode: "Zip Code",
  };

  return (
    <div className="page-content">
      <Container fluid>
        {billing ? <></> : <BreadCrumb title="Billing and Payment" pageTitle="Billing" />}

        {/* Billing Details Section */}
        {savedBillingDetails && !isEditing ? (
          // Display the billing details in a table if savedBillingDetails exists
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h3>Billing Details</h3>
              <Button variant="success" onClick={() => setIsEditing(true)}>
                Update
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover>

                <tbody>
                  {/* Displaying billing details with user-friendly labels */}
                  {Object.entries(savedBillingDetails)
                    .filter(([key]) => !['cognito_id', 'status', 'billing_plan'].includes(key)) // Exclude specific fields
                    .map(([key, value]) => (
                      <tr key={key}>
                        <td>{fieldLabels[key] || key}</td> {/* Use friendly label if available */}
                        <td>{value}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ) : (
          // Show the form if no savedBillingDetails or if the user is editing
          <Card className="mb-4">
            <Card.Header>
              <h3>Billing Details</h3>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="organizationType">
                      <Form.Label>Organization Type</Form.Label>
                      <Form.Control
                        as="select"
                        value={newBillingDetails.organization_type}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, organization_type: e.target.value })
                        }
                      >
                        <option value="">Select...</option>
                        <option value="Individual">Individual</option>
                        <option value="Organization">Organization</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="gstVat">
                      <Form.Label>GST/VAT Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="GST/VAT Number"
                        value={newBillingDetails.gst_number}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, gst_number: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="firstName">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="First name"
                        value={newBillingDetails.firstname}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, firstname: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Last name"
                        value={newBillingDetails.lastname}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, lastname: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="emailId">
                      <Form.Label>Email Id</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Email Id"
                        value={newBillingDetails.email}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, email: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="phoneNumber">
                      <Form.Label>Phone or Mobile Number</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="1234567890"
                        value={newBillingDetails.phone_number}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (/^\d{0,10}$/.test(newValue)) {
                            setNewBillingDetails({ ...newBillingDetails, phone_number: newValue });
                          }
                        }}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>



                <Form.Group controlId="country">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    as="select"
                    name="country"
                    value={newBillingDetails.country}
                    onChange={handleCountryChange}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.isoCode} value={country.isoCode}>
                        {country.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group controlId="state">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        as="select"
                        name="state"
                        value={newBillingDetails.state}
                        onChange={handleStateChange}
                        disabled={!states.length}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>


                  <Col md={6}>
                    <Form.Group controlId="city">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        as="select"
                        name="city"
                        value={newBillingDetails.city}
                        onChange={handleInputChange}
                        disabled={!cities.length}
                      >
                        <option value="">Select City</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="address">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Street address"
                        value={newBillingDetails.address}
                        onChange={(e) =>
                          setNewBillingDetails({ ...newBillingDetails, address: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="zipcode">
                      <Form.Label>Zipcode</Form.Label>
                      <Form.Control
                        type="text"
                        name="zipcode"
                        value={newBillingDetails.zipcode}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3 d-flex justify-content-center">
                  <Button variant="success" className="me-2" onClick={handleSaveBillingDetails}>
                    Save Billing Details
                  </Button>
                  <Button
                    onClick={() => setIsEditing(false)}
                    className="me-4"
                    style={{
                      backgroundColor: 'white',
                      border: '2px solid #6691E7',
                      color: '#6691E7',
                      width: '90px',
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}
        {/* Payment Method Section */}
        {
          billing ? <></>
            :
            <>
              <PaymentMethod />
              <BillingAlerts />
            </>
        }
        {/* Billing Alert Section */}

      </Container>
    </div>
  );
};

export default BillingDetails;
